import path from 'path';
import fs from 'fs';
import type { NextConfig } from 'next';
import {
  defaultWithGTConfigProps,
  defaultCacheExpiryTime,
} from './config-dir/props/defaultWithGTConfigProps';
import { type withGTConfigProps } from './config-dir/props/withGTConfigProps';
import {
  APIKeyMissingWarn,
  conflictingConfigurationBuildError,
  createBadFilepathWarning,
  createGTCompilerUnresolvedWarning,
  deprecatedLocaleMappingWarning,
  devApiKeyIncludedInProductionError,
  invalidCanonicalLocalesError,
  invalidLocalesError,
  projectIdMissingWarn,
  standardizedCanonicalLocalesWarning,
  standardizedLocalesWarning,
  unresolvedLoadDictionaryBuildError,
  unresolvedLoadTranslationsBuildError,
} from './errors/createErrors';
import {
  getLocaleProperties,
  isValidLocale,
  standardizeLocale,
} from '@generaltranslation/format';
import {
  rootParamStability,
  turboConfigStable,
} from './plugin/getStableNextVersionInfo';
import { validateCompiler } from './config-dir/utils/validateCompiler';
import {
  REQUEST_FUNCTION_ALIASES,
  resolveRequestFunctionPaths,
} from './config-dir/utils/resolveRequestFunctionPaths';
import { resolveConfigFilepath } from './config-dir/utils/resolveConfigFilepath';
import { ssgChecks } from './plugin/checks/ssgChecks';
import { cacheComponentsChecks } from './plugin/checks/cacheComponentsChecks';

type AutoderiveConfig = boolean | { jsx?: boolean; strings?: boolean };

type ConfigFileShape = {
  files?: {
    gt?: {
      parsingFlags?: {
        autoderive?: AutoderiveConfig;
        autoDerive?: AutoderiveConfig;
      };
    };
  };
};

type InternalGTConfigProps = withGTConfigProps &
  ConfigFileShape & {
    devApiKey?: string;
    loadDictionaryEnabled?: boolean;
    loadTranslationsType?: 'remote' | 'custom' | 'disabled';
    _dictionaryFileType?: string;
  };

type WithGTConfigResult<TNextConfig extends object> = TNextConfig & NextConfig;

/**
 * Initializes General Translation settings for a Next.js application.
 *
 * Use it in `next.config.js` to enable GT translation functionality as a plugin.
 *
 * @example
 * // In next.config.ts
 * import { withGTConfig } from 'gt-next/config';
 * import type { NextConfig } from 'next';
 *
 * const nextConfig = {
 *   reactStrictMode: true,
 * } satisfies NextConfig;
 *
 * export default withGTConfig(nextConfig, {
 *   projectId: 'abc-123',
 *   locales: ['en', 'es', 'fr'],
 *   defaultLocale: 'en'
 * })
 *
 * @param {string|undefined} config - Optional config filepath (defaults to './gt.config.json'). If a file is found, it will be parsed for GT config variables.
 * @param {string|undefined} dictionary - Optional dictionary configuration file path. If a string is provided, it will be used as a path.
 * @param {string} [apiKey=defaultInitGTProps.apiKey] - API key for the GeneralTranslation service. Required if using the default GT base URL.
 * @param {string} [devApiKey=defaultInitGTProps.devApiKey] - API key for dev environment only.
 * @param {string} [projectId=defaultInitGTProps.projectId] - Project ID for the GeneralTranslation service. Required for most functionality.
 * @param {string|null} [runtimeUrl=defaultInitGTProps.runtimeUrl] - The base URL for the GT API. Set to an empty string to disable automatic translations. Set to null to disable.
 * @param {string|null} [cacheUrl=defaultInitGTProps.cacheUrl] - The URL for cached translations. Set to null to disable.
 * @param {string[]|undefined} - Whether to use local translations.
 * @param {string[]} [locales=defaultInitGTProps.locales] - List of supported locales for the application.
 * @param {string} [defaultLocale=defaultInitGTProps.defaultLocale] - The default locale to use if none is specified.
 * @param {string|undefined} [getLocalePath="getLocale"] - The path to the custom getLocale function.
 * @param {string|undefined} [getRegionPath="getRegion"] - The path to the custom getRegion function.
 * @param {string|undefined} [getDomainPath="getDomain"] - The path to the custom getDomain function.
 * @param {object} [renderSettings=defaultInitGTProps.renderSettings] - Render settings for how translations should be handled.
 * @param {number} [cacheExpiryTime] - The time in milliseconds for how long translations should be cached.
 * @param {number} [maxConcurrentRequests=defaultInitGTProps.maxConcurrentRequests] - Maximum number of concurrent requests allowed.
 * @param {number} [maxBatchSize=defaultInitGTProps.maxBatchSize] - Maximum translation requests in the same batch.
 * @param {number} [batchInterval=defaultInitGTProps.batchInterval] - The interval in milliseconds between batched translation requests.
 * @param {boolean} [ignoreBrowserLocales=defaultWithGTConfigProps.ignoreBrowserLocales] - Whether to ignore browser's preferred locales.
 * @param {object} headersAndCookies - Additional headers and cookies that can be passed for extended configuration.
 * @param {boolean} [experimentalEnableSSG=false] - Whether to enable SSG.
 * @param {boolean} [disableSSGWarnings=defaultWithGTConfigProps.disableSSGWarnings] - Whether to disable SSG warnings. (deprecated)
 * @param {string|undefined} [getStaticLocalePath="getStaticLocale"] - The path to the static getLocale function. (deprecated)
 * @param {string|undefined} [getStaticRegionPath="getStaticRegion"] - The path to the static getRegion function. (deprecated)
 * @param {string|undefined} [getStaticDomainPath="getStaticDomain"] - The path to the static getDomain function. (deprecated)
 * @param {boolean} [experimentalLocaleResolution=defaultWithGTConfigProps.experimentalLocaleResolution] - Deprecated. Uses unsupported Next.js internals to infer locale from root params.
 * @param {string|undefined} [experimentalLocaleResolutionParam=defaultWithGTConfigProps.experimentalLocaleResolutionParam] - Deprecated. Only used by experimentalLocaleResolution.
 * @param {object} metadata - Additional metadata that can be passed for extended configuration.
 *
 * @param {object} nextConfig - The Next.js configuration object to extend
 * @param {withGTConfigProps} props - General Translation configuration properties
 * @returns {NextConfig} - An updated Next.js config with GT settings applied
 *
 * @throws {Error} If the project ID is missing and default URLs are used, or if the API key is required and missing.
 */
export function withGTConfig<TNextConfig extends object = NextConfig>(
  nextConfig?: TNextConfig,
  props: withGTConfigProps = {}
): WithGTConfigResult<TNextConfig> {
  const internalNextConfig = (nextConfig ?? {}) as unknown as NextConfig;

  // ---------- LOAD GT CONFIG FILE ---------- //

  let loadedConfig: Partial<InternalGTConfigProps> = {};
  try {
    let configPath: string | undefined;
    if (props.config) {
      configPath = props.config;
    } else if (fs.existsSync(defaultWithGTConfigProps.config)) {
      configPath = defaultWithGTConfigProps.config;
    } else if (fs.existsSync('./.gt/gt.config.json')) {
      // Support config under .gt for parity with .locadex
      configPath = './.gt/gt.config.json';
    } else if (fs.existsSync('./.locadex/gt.config.json')) {
      // Backward compatibility: support legacy .locadex directory
      configPath = './.locadex/gt.config.json';
    }
    if (typeof configPath === 'string' && fs.existsSync(configPath)) {
      const fileContent = fs.readFileSync(configPath, 'utf-8');
      loadedConfig = JSON.parse(fileContent);
    }
  } catch (error) {
    console.error('Error reading GT config file:', error);
  }

  // ---------- LOAD ENVIRONMENT VARIABLES ---------- //

  // resolve project ID
  const projectId: string | undefined = process.env.GT_PROJECT_ID;

  // resolve API keys
  const envApiKey: string | undefined =
    process.env.NODE_ENV === 'production'
      ? process.env.GT_API_KEY
      : process.env.GT_DEV_API_KEY || process.env.GT_API_KEY;
  let apiKey, devApiKey;
  if (envApiKey) {
    const apiKeyType = envApiKey?.split('-')?.[1];
    if (apiKeyType === 'api') {
      apiKey = envApiKey;
    } else if (apiKeyType === 'dev') {
      devApiKey = envApiKey;
    }
  }

  // conditionally add environment variables to config
  const envConfig: Partial<InternalGTConfigProps> = {
    ...(projectId ? { projectId } : {}),
    ...(apiKey ? { apiKey } : {}),
    ...(devApiKey ? { devApiKey } : {}),
  };

  // ---------- CHECK FOR CONFIG CONFLICTS ---------- //

  // Check for conflicts between config and params
  const propsRecord = props as Record<string, unknown>;
  const conflicts = Object.entries(loadedConfig)
    .filter(([key, value]) => {
      // Skip if key doesn't exist in props
      if (!(key in props)) return false;

      const propValue = propsRecord[key];

      // Handle null/undefined values
      if (value == null || propValue == null) {
        return value !== propValue;
      }

      // Handle primitive types (string, number, boolean)
      if (typeof value !== 'object') {
        return value !== propValue;
      }

      // Handle arrays (no need for deep equality check)
      if (Array.isArray(value)) {
        if (!Array.isArray(propValue)) return true;
        if (value.length !== propValue.length) return true;
        return value.some((v, i) => v !== propValue[i]);
      }

      // Handle objects
      if (typeof value === 'object' && typeof propValue === 'object') {
        const valueRecord = value as Record<string, unknown>;
        const propRecord = propValue as Record<string, unknown>;
        const valueKeys = Object.keys(valueRecord);
        const propKeys = Object.keys(propRecord);
        const keys = new Set([...valueKeys, ...propKeys]);

        // Objects must match exactly (no need to go deeper)
        if (valueKeys.length !== propKeys.length) return true;
        return !Array.from(keys).every((k) => valueRecord[k] === propRecord[k]);
      }

      return false;
    })
    .map(
      ([key, value]) =>
        `- Key: ${key} Next Config: ${JSON.stringify(propsRecord[key])} does not match GT Config: ${JSON.stringify(value)}`
    );

  if (conflicts.length) {
    throw new Error(conflictingConfigurationBuildError(conflicts));
  }

  // ---------- MERGE CONFIGS ---------- //

  // Merge cookie and header names
  const mergedHeadersAndCookies = {
    ...defaultWithGTConfigProps.headersAndCookies,
    ...props.headersAndCookies,
  };

  // Merge experimentalSwcPluginOptions
  const mergedExperimentalCompilerOptions = {
    ...defaultWithGTConfigProps.experimentalCompilerOptions,
    ...props.experimentalSwcPluginOptions,
    ...props.experimentalCompilerOptions,
  };

  // precedence: input > env > config file > defaults
  const mergedConfig: InternalGTConfigProps = {
    ...defaultWithGTConfigProps,
    ...loadedConfig,
    ...envConfig,
    ...props,
    headersAndCookies: mergedHeadersAndCookies,
    experimentalCompilerOptions: mergedExperimentalCompilerOptions,
    _usingPlugin: true, // flag to indicate plugin usage
  };

  // clear up any issues with the compiler options
  validateCompiler(mergedConfig);

  // ----------- RESOLVE ANY EXTERNAL FILES ----------- //

  // Resolve wasm filepath
  const turboPackEnabled = !!process.env.TURBOPACK;
  let resolvedWasmFilePath = '';
  if (mergedConfig.experimentalCompilerOptions?.type === 'swc') {
    try {
      if (turboPackEnabled) {
        const absolutePath = path.resolve(__dirname, './gt_swc_plugin.wasm');
        resolvedWasmFilePath =
          './' + path.relative(process.cwd(), absolutePath).replace(/\\/g, '/');
      } else {
        resolvedWasmFilePath = path.resolve(__dirname, './gt_swc_plugin.wasm');
      }
    } catch (error) {
      console.error(
        createGTCompilerUnresolvedWarning('swc'),
        'Error message:',
        error
      );
      resolvedWasmFilePath = '';
      mergedConfig.experimentalCompilerOptions.type = 'none';
    }
  }

  // Resolve dictionary filepath
  let resolvedDictionaryFilePath =
    typeof mergedConfig.dictionary === 'string'
      ? mergedConfig.dictionary
      : resolveConfigFilepath('dictionary', ['.ts', '.js', '.json']); // fallback to dictionary

  // Check [defaultLocale].json file
  if (!resolvedDictionaryFilePath && mergedConfig.defaultLocale) {
    resolvedDictionaryFilePath = resolveConfigFilepath(
      mergedConfig.defaultLocale,
      ['.json']
    );

    // Check [defaultLanguageCode].json file
    if (!resolvedDictionaryFilePath) {
      const defaultLanguage = getLocaleProperties(
        mergedConfig.defaultLocale
      )?.languageCode;

      if (defaultLanguage && defaultLanguage !== mergedConfig.defaultLocale) {
        resolvedDictionaryFilePath = resolveConfigFilepath(defaultLanguage, [
          '.json',
        ]);
      }
    }
  }

  // Get the type of dictionary file
  const resolvedDictionaryFilePathType = resolvedDictionaryFilePath
    ? path.extname(resolvedDictionaryFilePath)
    : undefined;
  if (resolvedDictionaryFilePathType) {
    mergedConfig._dictionaryFileType = resolvedDictionaryFilePathType;
  }

  // Resolve custom dictionary loader path
  const customLoadDictionaryPath =
    typeof mergedConfig.loadDictionaryPath === 'string'
      ? mergedConfig.loadDictionaryPath
      : resolveConfigFilepath('loadDictionary');

  // Resolve custom translation loader path
  const customLoadTranslationsPath =
    typeof mergedConfig.loadTranslationsPath === 'string'
      ? mergedConfig.loadTranslationsPath
      : resolveConfigFilepath('loadTranslations');

  // Resolve request function paths
  const requestFunctionPaths = resolveRequestFunctionPaths(mergedConfig);

  // Warn if found in /app directory
  if (
    !resolvedDictionaryFilePath &&
    resolveConfigFilepath('dictionary', ['.ts', '.js', '.json'], undefined, [
      './app',
      './src/app',
    ])
  ) {
    console.warn(
      createBadFilepathWarning('dictionary', ['./app', './src/app'])
    );
  }

  if (
    !customLoadDictionaryPath &&
    resolveConfigFilepath(
      'loadDictionary',
      ['.ts', '.js', '.json'],
      undefined,
      ['./app', './src/app']
    )
  ) {
    console.warn(
      createBadFilepathWarning('loadDictionary', ['./app', './src/app'])
    );
  }

  if (
    !customLoadTranslationsPath &&
    resolveConfigFilepath(
      'loadTranslations',
      ['.ts', '.js', '.json'],
      undefined,
      ['./app', './src/app']
    )
  ) {
    console.warn(
      createBadFilepathWarning('loadTranslations', ['./app', './src/app'])
    );
  }

  // ----------- LOCALE STANDARDIZATION ----------- //

  // Check if using Services
  const gtRuntimeTranslationEnabled = !!(
    mergedConfig.runtimeUrl === defaultWithGTConfigProps.runtimeUrl &&
    ((process.env.NODE_ENV === 'production' && mergedConfig.apiKey) ||
      (process.env.NODE_ENV === 'development' && mergedConfig.devApiKey))
  );
  const gtRemoteCacheEnabled = !!(
    mergedConfig.cacheUrl === defaultWithGTConfigProps.cacheUrl &&
    mergedConfig.loadTranslationsType === 'remote'
  );
  const gtServicesEnabled = !!(
    (gtRuntimeTranslationEnabled || gtRemoteCacheEnabled) &&
    mergedConfig.projectId
  );

  // Standardize locales
  if (mergedConfig.locales && mergedConfig.defaultLocale) {
    mergedConfig.locales.unshift(mergedConfig.defaultLocale);
  }
  const updatedLocales: string[] = [];
  mergedConfig.locales = Array.from(new Set(mergedConfig.locales)).map(
    (locale) => {
      const updatedLocale = gtServicesEnabled
        ? standardizeLocale(locale)
        : locale;
      if (updatedLocale !== locale) {
        updatedLocales.push(`${locale} -> ${updatedLocale}`);
      }
      return updatedLocale;
    }
  );

  // Standardize canonical locales
  const updatedCanonicalLocales: string[] = [];
  if (mergedConfig.customMapping) {
    mergedConfig.customMapping = Object.fromEntries(
      Object.entries(mergedConfig.customMapping).map(([key, value]) => {
        if (typeof value !== 'object' || !('code' in value)) {
          return [key, value];
        }
        const updatedLocale = gtServicesEnabled
          ? standardizeLocale((value as { code: string }).code)
          : (value as { code: string }).code;
        if (updatedLocale !== (value as { code: string }).code) {
          updatedCanonicalLocales.push(`${key} -> ${updatedLocale}`);
        }
        return [
          key,
          {
            ...value,
            code: updatedLocale,
          },
        ];
      })
    );
  }

  // Run SSG checks
  ssgChecks(mergedConfig, requestFunctionPaths);

  // Run cache component checks
  cacheComponentsChecks({
    mergedConfig,
    nextConfig: internalNextConfig,
    requestFunctionPaths,
    localTranslationsEnabled: !!customLoadTranslationsPath,
    localDictionaryEnabled: !!customLoadDictionaryPath,
  });

  // ---------- DERIVED CONFIG ATTRIBUTES ---------- //

  // Local dictionary flag
  if (customLoadDictionaryPath) {
    // Check: file exists if provided
    if (!fs.existsSync(path.resolve(customLoadDictionaryPath))) {
      throw new Error(
        unresolvedLoadDictionaryBuildError(customLoadDictionaryPath)
      );
    } else {
      mergedConfig.loadDictionaryEnabled = true;
    }
  } else {
    mergedConfig.loadDictionaryEnabled = false;
  }

  // Local translations flag
  if (customLoadTranslationsPath) {
    // Check: file exists if provided
    if (!fs.existsSync(path.resolve(customLoadTranslationsPath))) {
      throw new Error(
        unresolvedLoadTranslationsBuildError(customLoadTranslationsPath)
      );
    } else {
      mergedConfig.loadTranslationsType = 'custom';
    }
  } else {
    mergedConfig.loadTranslationsType = 'remote';
  }

  // Set default cache expiry if and only if no dev key
  if (
    mergedConfig.loadTranslationsType == 'remote' &&
    !mergedConfig.devApiKey &&
    typeof mergedConfig.cacheExpiryTime === 'undefined'
  ) {
    mergedConfig.cacheExpiryTime = defaultCacheExpiryTime;
  }

  // ---------- ERROR CHECKS ---------- //

  // Check: using deprecated localeMapping
  if (props.customMapping) {
    console.warn(deprecatedLocaleMappingWarning);
  }

  // Check: invalid locale
  if (!mergedConfig.customMapping && gtServicesEnabled) {
    const invalidLocales: string[] = [];
    mergedConfig.locales.forEach((locale) => {
      if (!isValidLocale(locale)) {
        invalidLocales.push(locale);
      }
    });
    if (invalidLocales.length) {
      throw new Error(invalidLocalesError(invalidLocales));
    }
  }

  // Check: invalid canonical locale
  if (mergedConfig.customMapping && gtServicesEnabled) {
    const invalidCanonicalLocales: string[] = [];
    mergedConfig.locales.forEach((locale) => {
      if (!isValidLocale(locale, mergedConfig.customMapping)) {
        invalidCanonicalLocales.push(locale);
      }
    });
    if (invalidCanonicalLocales.length) {
      throw new Error(invalidCanonicalLocalesError(invalidCanonicalLocales));
    }
  }

  // Check: projectId is not required for remote infrastructure, but warn if missing for dev, nothing for prod
  if (
    (mergedConfig.cacheUrl || mergedConfig.runtimeUrl) &&
    !mergedConfig.projectId &&
    process.env.NODE_ENV === 'development' &&
    mergedConfig.loadTranslationsType === 'remote' &&
    !mergedConfig.loadDictionaryEnabled // skip warn if using local dictionary
  ) {
    console.warn(projectIdMissingWarn);
  }

  // Check: dev API key should not be included in production
  if (process.env.NODE_ENV === 'production' && mergedConfig.devApiKey) {
    throw new Error(devApiKeyIncludedInProductionError);
  }

  // Check: An API key is required for runtime translation
  if (
    mergedConfig.projectId && // must have projectId for this check to matter anyways
    mergedConfig.runtimeUrl &&
    !(mergedConfig.apiKey || mergedConfig.devApiKey) &&
    process.env.NODE_ENV === 'development'
  ) {
    console.warn(APIKeyMissingWarn);
  }

  // Check: if using GT infrastructure, warn about unsupported locales
  if (gtServicesEnabled) {
    // Warn about standardized locales
    if (updatedLocales.length) {
      console.warn(standardizedLocalesWarning(updatedLocales));
    }

    // Warn about standardized canonical locales
    if (updatedCanonicalLocales.length) {
      console.warn(
        standardizedCanonicalLocalesWarning(updatedCanonicalLocales)
      );
    }
  }

  // ---------- STORE CONFIGURATIONS ---------- //
  const I18NConfigParams = JSON.stringify(mergedConfig);

  const { type: _type, ...compilerOptions } =
    mergedConfig.experimentalCompilerOptions || {};

  // Read autoderive from parsingFlags (single source of truth shared with CLI)
  const rawAutoderive: boolean | { jsx?: boolean; strings?: boolean } =
    loadedConfig?.files?.gt?.parsingFlags?.autoderive ??
    loadedConfig?.files?.gt?.parsingFlags?.autoDerive ??
    false;
  const autoderiveJsx =
    typeof rawAutoderive === 'boolean'
      ? rawAutoderive
      : (rawAutoderive.jsx ?? false);
  const autoderiveStrings =
    typeof rawAutoderive === 'boolean'
      ? rawAutoderive
      : (rawAutoderive.strings ?? false);

  const swcPluginOptions: Record<string, unknown> = {
    ...compilerOptions,
    autoderiveJsx,
    autoderiveStrings,
  };

  const swcPluginEntry: [string, Record<string, unknown>] | null =
    mergedConfig.experimentalCompilerOptions?.type === 'swc'
      ? [resolvedWasmFilePath, swcPluginOptions]
      : null;

  const turboAliases = {
    'gt-next/_dictionary': resolvedDictionaryFilePath || '',
    'gt-next/_load-translations': customLoadTranslationsPath || '',
    'gt-next/_load-dictionary': customLoadDictionaryPath || '',
    ...Object.fromEntries(
      Object.entries(requestFunctionPaths).map(([functionName, path]) => {
        return [
          REQUEST_FUNCTION_ALIASES[
            functionName as keyof typeof REQUEST_FUNCTION_ALIASES
          ],
          path,
        ];
      })
    ),
  };

  // experimental.turbo is deprecated in next@15.3.0.
  // Check for experimental.turbo. If we write to turbopack field, experimental fields will be ignored.
  // Yet, if there are other resolveAlias fields, we don't want to be ignored either.
  const experimentalTurbopack = !(
    turboConfigStable &&
    (!internalNextConfig.experimental?.turbo ||
      internalNextConfig.turbopack?.resolveAlias)
  );

  const config: NextConfig = {
    ...internalNextConfig,
    env: {
      ...internalNextConfig.env,
      _GENERALTRANSLATION_I18N_CONFIG_PARAMS: I18NConfigParams,
      ...(resolvedDictionaryFilePathType && {
        _GENERALTRANSLATION_DICTIONARY_FILE_TYPE:
          resolvedDictionaryFilePathType,
      }),
      _GENERALTRANSLATION_LOCAL_DICTIONARY_ENABLED:
        mergedConfig.loadDictionaryEnabled.toString(),
      _GENERALTRANSLATION_LOCAL_TRANSLATION_ENABLED: (
        mergedConfig.loadTranslationsType === 'custom'
      ).toString(),
      _GENERALTRANSLATION_DEFAULT_LOCALE: (
        mergedConfig.defaultLocale ||
        defaultWithGTConfigProps.defaultLocale ||
        ''
      ).toString(),
      _GENERALTRANSLATION_GT_SERVICES_ENABLED: gtServicesEnabled.toString(),
      _GENERALTRANSLATION_IGNORE_BROWSER_LOCALES:
        mergedConfig.ignoreBrowserLocales?.toString() ||
        defaultWithGTConfigProps.ignoreBrowserLocales?.toString() ||
        'false',
      _GENERALTRANSLATION_CUSTOM_GET_LOCALE_ENABLED:
        requestFunctionPaths.getLocale ? 'true' : 'false',
      _GENERALTRANSLATION_CUSTOM_GET_REGION_ENABLED:
        requestFunctionPaths.getRegion ? 'true' : 'false',
      _GENERALTRANSLATION_CUSTOM_GET_DOMAIN_ENABLED:
        requestFunctionPaths.getDomain ? 'true' : 'false',
      _GENERALTRANSLATION_STATIC_GET_LOCALE_ENABLED:
        requestFunctionPaths.getStaticLocale ? 'true' : 'false',
      _GENERALTRANSLATION_STATIC_GET_REGION_ENABLED:
        requestFunctionPaths.getStaticRegion ? 'true' : 'false',
      _GENERALTRANSLATION_STATIC_GET_DOMAIN_ENABLED:
        requestFunctionPaths.getStaticDomain ? 'true' : 'false',
      _GENERALTRANSLATION_DISABLE_SSG_WARNINGS:
        mergedConfig.disableSSGWarnings?.toString() || 'false',
      _GENERALTRANSLATION_ENABLE_SSG:
        mergedConfig.experimentalEnableSSG?.toString() || 'false',
      _GENERALTRANSLATION_EXPERIMENTAL_LOCALE_RESOLUTION:
        mergedConfig.experimentalLocaleResolution?.toString() || 'false',
      _GENERALTRANSLATION_EXPERIMENTAL_LOCALE_RESOLUTION_PARAM:
        mergedConfig.experimentalLocaleResolutionParam,
    },
    ...(turboPackEnabled &&
      !experimentalTurbopack && {
        turbopack: {
          ...internalNextConfig.turbopack,
          resolveAlias: {
            ...internalNextConfig.turbopack?.resolveAlias,
            ...turboAliases,
          },
        },
      }),
    experimental: {
      ...internalNextConfig.experimental,
      ...(rootParamStability === 'experimental' && {
        rootParams: true,
      }),
      swcPlugins: [
        ...(internalNextConfig.experimental?.swcPlugins || []),
        ...(swcPluginEntry ? [swcPluginEntry] : []),
      ],
      ...(turboPackEnabled &&
        experimentalTurbopack && {
          turbo: {
            ...internalNextConfig.experimental?.turbo,
            resolveAlias: {
              ...internalNextConfig.experimental?.turbo?.resolveAlias,
              ...turboAliases,
            },
          },
        }),
    },
    webpack: function webpack(
      ...[webpackConfig, options]: Parameters<
        NonNullable<NextConfig['webpack']>
      >
    ) {
      // Only apply webpack aliases if we're using webpack (not Turbopack)
      if (!turboPackEnabled) {
        // Try to load GT compiler if available
        if (mergedConfig.experimentalCompilerOptions?.type === 'babel') {
          try {
            const {
              webpack: gtUnplugin,
            } = require('@generaltranslation/compiler');
            webpackConfig.plugins.unshift(
              gtUnplugin(mergedConfig.experimentalCompilerOptions || {})
            );
          } catch (e) {
            mergedConfig.experimentalCompilerOptions.type = 'none';
            console.warn(
              createGTCompilerUnresolvedWarning('babel'),
              'Error message:',
              e
            );
          }
        }

        // Disable cache in dev bc people might move around loadTranslations() and loadDictionary() files
        if (process.env.NODE_ENV === 'development') {
          webpackConfig.cache = false;
        }
        if (resolvedDictionaryFilePath) {
          webpackConfig.resolve.alias['gt-next/_dictionary'] = path.resolve(
            webpackConfig.context,
            resolvedDictionaryFilePath
          );
        }
        if (customLoadTranslationsPath) {
          webpackConfig.resolve.alias[`gt-next/_load-translations`] =
            path.resolve(webpackConfig.context, customLoadTranslationsPath);
        }
        if (customLoadDictionaryPath) {
          webpackConfig.resolve.alias[`gt-next/_load-dictionary`] =
            path.resolve(webpackConfig.context, customLoadDictionaryPath);
        }
        for (const [functionName, pathString] of Object.entries(
          requestFunctionPaths
        )) {
          const key =
            REQUEST_FUNCTION_ALIASES[
              functionName as keyof typeof REQUEST_FUNCTION_ALIASES
            ];
          webpackConfig.resolve.alias[key] = path.resolve(
            webpackConfig.context,
            pathString
          );
        }
      }
      if (typeof internalNextConfig?.webpack === 'function') {
        return internalNextConfig.webpack(webpackConfig, options);
      }
      return webpackConfig;
    },
  };
  return config as WithGTConfigResult<TNextConfig>;
}

// Keep initGT for backward compatibility
export const initGT =
  (props: withGTConfigProps) =>
  <TNextConfig extends object = NextConfig>(nextConfig?: TNextConfig) =>
    withGTConfig(nextConfig, props);
